var React = require('react');
let DnsRecordSet = require('./host_helper/dns_record_set');

/*
 * This component displays the current state of host dns records for a given domain.
 *
 * @param {string} props.domain - The domain to provide help for.
 * @param {string} props.subdomain - the subdomain of the page, if any.
 * @param {bool}   props.subdomain_forced - true iff one cannot modify or use the vanity subdomain.
 * @param {bool}   props.active - false iff we should display a notice that the domain is not active until the person activates.
 * @param {string} props.page_code - the code of the page; used to generate the page-specific json url.
 * @param {string} props.expected_endpoint - the ideal endpoint the user should CNAME to.
 * @param {int}    props.expected_ttl - the suggested TTL for a new DNS record.
 * @param {string} props.status - unset, deprecated, invalid, or valid.
 * @param {bool}   props.detailed_view - true to force the detailed view of the domain status.
 * @param {array}  props.actual_records - A list of dns records with the fields 'type', 'ttl', 'value', and 'hints'.
 */
class HostHelper extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      'page_code': this.props.page_code,
      'domain': this.props.domain,
      'expected_endpoint': this.props.expected_endpoint,
      'expected_ttl': this.props.expected_ttl,
      'actual_records': this.props.actual_records,
      'status': this.props.status,
      'folded': this.props.status === 'unset' || this.props.status == 'valid',
      'detailed_view': this.props.detailed_view,
      'subdomain': this.props.subdomain,
      'subdomain_forced': this.props.subdomain_forced,
      'active': this.props.active
    };
  }


  // On hitting the revalidate button or link, pull the new state as a json and set local state to that.
  _submit() {
    $.ajax({
      type: "GET",
      url: Routes.domain_state_page_url(this.state.page_code)
    }).success((msg) => {
      // If we do not notify and nothing changes, it looks like nothing happened.
      HRB.utils.notify('Current DNS state refreshed successfully.');
      this.setState(msg);
    }).fail(() => {
      HRB.utils.basicFailAndReload();
    });
  }

  // Switch between folded and unfolded
  _toggleFolding() {
    this.setState({folded: !this.state.folded});
  }

  _foldedClass() {
    return this.state.folded ? 'folded' : 'unfolded';
  }

  _statusClass() {
    return 'domain-' + this.state.status;
  }

  // This is the view that most clients see.
  // It does not display anything about the inbound DNS but the expected DNS row
  // and whether or not the actual endpoint matches it exactly.
  _renderLegacyView() {
    let contents;
    let configUrl = Routes.edit_page_url(this.props.page_code, {'anchor': 'tab-dns-config'});
    if (this.state.status === 'unset') {
      contents = <span><strong>Directions</strong>: create a CNAME DNS record and point it at <em>{this.state.expected_endpoint}</em>.</span>;
    } else if (this.state.status === 'valid') {
      contents = <span className="checkmark-inline"> DNS is correctly configured! <span className="monospace">{this.state.domain}</span> points at <span className="monospace">{this.state.expected_endpoint}</span></span>;
    } else {
      contents = (
        <span className="alert-inline">
          &nbsp;Please ensure your DNS entry for <span className="monospace">{this.state.domain}</span> is a <span className="monospace">CNAME</span> entry pointing at <span className="monospace">{this.state.expected_endpoint}</span>.
        </span>
      );
    }

    let activeMessage =  this.state.active ? null :
      <span>
        <br/>
        <strong>Note:</strong> your status page will redirect to your unique subdomain <em>({this.state.subdomain})</em> until your page launches.
      </span>;

    let revalidate_link = this.state.status === 'unset' ? null : <a id="dns-revalidation-button" href="#" onClick={this._submit.bind(this)}>Revalidate DNS</a>;

    return (<p className="help-block">
      {contents}&nbsp;{revalidate_link}
      {activeMessage}
    </p>);
  }

  // Returns a DnsRecordSet representing the expected set of records at the custom domain.
  _expectedCname() {
    return <DnsRecordSet domain={this.state.domain ? this.state.domain : 'Expected Domain'} show_header={true} show_ttl={true} records={[{
      type: 'CNAME',
      ttl: this.state.expected_ttl,
      value: this.state.expected_endpoint,
      hints: []
    }]}/>;
  }

  // This is the detailed view.
  // The header contains the status and a 'folder' button to show more detail.
  // The detail contains all the records found at the custom domain, hints to resolve an invalid record,
  //   explanations of deprecation, the expected CNAME record, and a button to revalidate without refreshing.
  _renderDetailedView() {
    // The funky &nbsp; insertions are to correct for odd spacing before and after spans.
    // We only revalidate if we expect a DNS entry, so we can short-cut this case.
    var contents, header = <span>{this.state.status}</span>;
    if (this.state.status === 'unset') {
      contents = <div>To prepare to serve from a custom domain immediately, insert the following record at that domain: {this._expectedCname()}</div>;
    } else {
      let revalidate_button = (
        <a id="dns-revalidation-button"
           className="cpt-button style-primary size-small"
           onClick={this._submit.bind(this)}
           href="#">Revalidate DNS</a>
      );

      // Do not show the TTL on the records here. This is the TTL in the DNS cache we use, not the TTL of the record
      // on the authoritative server.
      let actual_records = this.state.actual_records.length > 0 ?
          <div>
            We found the following records at <span className="monospace">{this.state.domain}</span>:
            <DnsRecordSet domain={this.state.domain} show_header={true} show_ttl={false} records={this.state.actual_records}/>
          </div>
          :
          <div>We were unable to find any records at <span className="monospace">{this.state.domain}</span>.</div>
        ;

      // Only show the actual records if they are different than expected.
      if (this.state.status === 'valid') {
        contents = (
          <div>
            {actual_records}
            {revalidate_button}
          </div>
        );
      } else {
        contents = (
          <div>
            <div>We require this DNS record: {this._expectedCname()}</div>
            {actual_records}
            {revalidate_button}
          </div>
        );
      }
    }

    return (
      <div>
        {this._renderLegacyView()}
        <div className="domain-helper">
          <div className={'domain-header ' + this._statusClass() + ' ' + this._foldedClass()}>
            <a id="domain-folder" className={this._foldedClass()} onClick={this._toggleFolding.bind(this)} href="#"></a>
            <div className="domain-helper-title">{this.state.status.toUpperCase()}</div>
            <i className="domain-status-icon"></i>
          </div>
          <div className={'domain-content ' + this._statusClass() + ' ' + this._foldedClass()}>
            {contents}
            <div style={{display: 'block', clear: 'both', float: 'none'}}></div>
          </div>
        </div>
      </div>
    )
  }

  render() {
    if (this.state.subdomain_forced) {
      return <p className="help-block">This field is disabled because the privacy feature you are using requires you to use the subdomain URL.</p>;
    } else if (this.state.detailed_view) {
      return this._renderDetailedView();
    } else {
      return this._renderLegacyView();
    }
  }
}

module.exports = HostHelper;



// WEBPACK FOOTER //
// ./app/assets/javascripts/components/manage/pages/host_helper.jsx